import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../shared/utils.js/axiosConfig";

const initialState = {
  employeeLoans: [],
  currentEmployeeLoan: null,
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  },
  loading: false,
  error: null,
  success: null,
};

// Fetch all employee loans with pagination and filters
export const fetchEmployeeLoans = createAsyncThunk(
  "employeeLoans/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/employee-loans", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch single employee loan by ID
export const fetchEmployeeLoanById = createAsyncThunk(
  "employeeLoans/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/employee-loans/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Create new employee loan
export const createEmployeeLoan = createAsyncThunk(
  "employeeLoans/create",
  async (loanData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/employee-loans", loanData);
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Update employee loan
export const updateEmployeeLoan = createAsyncThunk(
  "employeeLoans/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/employee-loans/${id}`, data);
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Delete employee loan
export const deleteEmployeeLoan = createAsyncThunk(
  "employeeLoans/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/employee-loans/${id}`);
      return { id, ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Approve employee loan
export const approveEmployeeLoan = createAsyncThunk(
  "employeeLoans/approve",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/employee-loans/${id}/approve`,
      );
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Cancel employee loan
export const cancelEmployeeLoan = createAsyncThunk(
  "employeeLoans/cancel",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/employee-loans/${id}/cancel`);
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const employeeLoansSlice = createSlice({
  name: "employeeLoans",
  initialState,
  reducers: {
    clearCurrentEmployeeLoan: state => {
      state.currentEmployeeLoan = null;
    },
    clearError: state => {
      state.error = null;
    },
    clearSuccess: state => {
      state.success = null;
    },
    hydrate: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      // Fetch all
      .addCase(fetchEmployeeLoans.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeLoans.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeLoans = action.payload.data || [];
        state.pagination =
          action.payload.meta ||
          action.payload.pagination ||
          initialState.pagination;
      })
      .addCase(fetchEmployeeLoans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch by ID
      .addCase(fetchEmployeeLoanById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeLoanById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEmployeeLoan = action.payload.data;
      })
      .addCase(fetchEmployeeLoanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createEmployeeLoan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEmployeeLoan.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          state.employeeLoans.unshift(action.payload.data);
          state.pagination.total += 1;
        }
      })
      .addCase(createEmployeeLoan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateEmployeeLoan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmployeeLoan.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.employeeLoans.findIndex(
          loan => loan.id === action.payload.data?.id,
        );
        if (index !== -1 && action.payload.data) {
          state.employeeLoans[index] = action.payload.data;
        }
      })
      .addCase(updateEmployeeLoan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteEmployeeLoan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEmployeeLoan.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeLoans = state.employeeLoans.filter(
          loan => loan.id !== action.payload.id,
        );
        state.pagination.total -= 1;
      })
      .addCase(deleteEmployeeLoan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Approve
      .addCase(approveEmployeeLoan.pending, state => {
        state.loading = true;
      })
      .addCase(approveEmployeeLoan.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.employeeLoans.findIndex(
          loan => loan.id === action.payload.data?.id,
        );
        if (index !== -1 && action.payload.data) {
          state.employeeLoans[index] = action.payload.data;
        }
      })
      .addCase(approveEmployeeLoan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Cancel
      .addCase(cancelEmployeeLoan.pending, state => {
        state.loading = true;
      })
      .addCase(cancelEmployeeLoan.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.employeeLoans.findIndex(
          loan => loan.id === action.payload.data?.id,
        );
        if (index !== -1 && action.payload.data) {
          state.employeeLoans[index] = action.payload.data;
        }
      })
      .addCase(cancelEmployeeLoan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentEmployeeLoan, clearError, clearSuccess } =
  employeeLoansSlice.actions;
export default employeeLoansSlice.reducer;
